import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
export type ToastLabel = 'Success' | 'Error' | 'Info';

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: ToastLabel;
  message: string | null | undefined;
  semanticId?: string;
}

const labelColors: Record<ToastLabel, string> = {
  Success: '#05B937',
  Error: '#DD2525',
  Info: '#1C85E7',
};

const labelIcons: Record<ToastLabel, string> = {
  Success: 'check_circle',
  Error: 'block',
  Info: 'error',
};

const NotificationWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$labelType'].includes(String(prop)),
})<{ $labelType: ToastLabel }>`
  display: flex;
  gap: ${rem(10)};
  align-items: center;
  justify-content: space-between;

  .toast-icon {
    font-size: ${rem(24)};
    color: ${({ $labelType }) => labelColors[$labelType]};
  }

  .closeIcon {
    color: var(--ui-border);
    font-size: ${rem(20)};
  }
`;

const NotificationContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['$labelType'].includes(String(prop)),
})<{ $labelType: ToastLabel }>`
  .notification-title {
    display: flex;
    align-items: center;
    font-size: ${rem(16)};
    font-weight: 500;
    line-height: ${rem(18)};
    padding-bottom: ${rem(3)};
    color: ${({ $labelType }) => labelColors[$labelType]};
  }

  .notification-message {
    display: flex;
    align-items: center;
    font-size: ${rem(14)};
    font-weight: 400;
    line-height: ${rem(18)};
  }
`;

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(function Toast(
  { label = 'Info', message, semanticId, ...rest },
  ref
) {
  const semanticProps = getSemanticAttributes(semanticId);

  return (
    <NotificationWrapper ref={ref} $labelType={label} {...semanticProps} {...rest}>
      <div style={{ display: 'flex', gap: rem(16), alignItems: 'center' }}>
        <span className='material-symbols-outlined toast-icon'>{labelIcons[label]}</span>
        <NotificationContent $labelType={label}>
          <p className='notification-title'>
            {label} {label !== 'Info' && 'Message'}
          </p>
          <p className='notification-message'>{message}</p>
        </NotificationContent>
      </div>
    </NotificationWrapper>
  );
});

Toast.displayName = 'Toast';
